import React, { useState, useContext, useEffect, useMemo } from "react";
import { FaEdit, FaTrash, FaPlus, FaHome, FaExclamationTriangle, FaMapMarkerAlt } from "react-icons/fa";
import Iller from "../../il-ilce.json";
import countryCodesData from '../../constants/countryCodes.json';
import CountrySelector from '../CountrySelector';
// import { UserContext } from "../../context/UserContext.js";
import { useToast } from "../../context/ToastContext.js";
import "./AddressComponent.css";
import {
  useGetAddressesQuery,
  useAddAddressMutation,
  useUpdateAddressMutation,
  useDeleteAddressMutation,
} from "../../services/userApi.js";
import Loader from "../Loader.js";

const AddressComponent = () => {
  // const { getAddresses, addAddress, updateAddress, deleteAddress } = useContext(UserContext);
  const { data: addressesData, isLoading: isLoadingAddresses, isError: isErrorAddresses, refetch: refetchAddresses } = useGetAddressesQuery();
  const [addAddress, { isLoading: isAddingAddress }] = useAddAddressMutation();
  const [updateAddress, { isLoading: isUpdatingAddress }] = useUpdateAddressMutation();
  const [deleteAddress, { isLoading: isDeletingAddress }] = useDeleteAddressMutation();

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    id: null,
    title: "",
    ad: "",
    soyad: "",
    email: "",
    telefon: "",
    ulke_kodu: "TR",
    tc: "",
    dogum_tarihi: "",
    addressText: "",
    il: "",
    ilce: "",
    adres_tipi: "Teslimat Adresi"
  });
  const [iller, setIller] = useState(Iller.iller);
  const [ilceler, setIlceler] = useState([]);
  
  // Country codes setup
  const countryCodes = useMemo(() => {
    const turkey = countryCodesData.find(c => c.code === 'TR');
    const others = countryCodesData.filter(c => c.code !== 'TR').sort((a, b) => a.name.localeCompare(b.name));
    return [turkey, ...others];
  }, []);
  
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const { showToast } = useToast();

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        // const data = await getAddresses();
        // setAddresses(data || []);
        if (addressesData) {
          setAddresses(addressesData || []);
        }
      } catch (error) {
        console.error("Adresler yüklenirken hata:", error);
        showToast("Adresler yüklenirken bir hata oluştu", "error");
      } finally {
        setLoading(false);
      }
    };

    // fetchAddresses();
    // }, [getAddresses, showToast]);
    if (isLoadingAddresses) {
      setLoading(true);
    } else {
      setLoading(false);
      if (addressesData) {
        setAddresses(addressesData || []);
      }
      if (isErrorAddresses) {
        console.error("Adresler yüklenirken hata:", isErrorAddresses);
        showToast("Adresler yüklenirken bir hata oluştu", "error");
      }
    }
  }, [addressesData, isLoadingAddresses, isErrorAddresses, showToast]);

  const openAddressModal = (address = null) => {
    if (address) {
      setEditingAddress(address.id);
      setAddressForm({
        id: address.id,
        title: address.title,
        ad: address.ad,
        soyad: address.soyad,
        email: address.email,
        telefon: address.telefon || "",
        ulke_kodu: address.ulke_kodu || "TR",
        tc: address.tc || "",
        dogum_tarihi: normalizeDateForInput(address.dogum_tarihi),
        addressText: address.addressText,
        il: address.il || "",
        ilce: address.ilce || "",
        adres_tipi: address.adres_tipi || "Teslimat Adresi"
      });

      // Try to find the country code from the address or default to TR
      const country = countryCodes.find(c => c.code === (address.ulke_kodu || "TR"));
      if (country) {
        setSelectedCountry(country);
      }

      if (address.il) {
        const secilenIl = iller.find((il) => il.il_adi === address.il);
        setIlceler(secilenIl ? secilenIl.ilceler : []);
      }
    } else {
      setEditingAddress(null);
      setAddressForm({
        id: null,
        title: "",
        ad: "",
        soyad: "",
        email: "",
        telefon: "",
        ulke_kodu: "TR",
        tc: "",
        dogum_tarihi: "",
        addressText: "",
        il: "",
        ilce: "",
        adres_tipi: "Teslimat Adresi"
      });
      setSelectedCountry(countryCodes[0]);
      setIlceler([]);
    }
    setShowAddressModal(true);
  };

  const closeAddressModal = () => {
    setShowAddressModal(false);
    setEditingAddress(null);
  };

  const handlePhoneKeyDown = (e) => {
    // Basic key handling if needed, but strict "05" enforcement is removed
  };

  const handlePhoneInput = (e) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const maxLength = selectedCountry.mobile_number_length || 15;

    if (sanitizedValue.length > maxLength) {
      return;
    }

    setAddressForm(prev => ({ ...prev, telefon: sanitizedValue }));
  };

  const formatPhoneNumber = (number) => {
    return number; // Raw number for now, formatting can be added later based on country
  };

  const normalizeDateForInput = (value) => {
    if (!value) return '';

    const normalizeParts = (parts, order) => {
      const map = { day: parts[0], month: parts[1], year: parts[2] };
      if (order === 'YMD') {
        map.year = parts[0];
        map.month = parts[1];
        map.day = parts[2];
      } else if (order === 'DMY') {
        map.day = parts[0];
        map.month = parts[1];
        map.year = parts[2];
      }
      const { day, month, year } = map;
      if (!day || !month || !year) return '';
      return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    };

    if (value instanceof Date && !isNaN(value)) {
      return value.toISOString().split('T')[0];
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';

      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }

      if (/^\d{2}[./-]\d{2}[./-]\d{4}$/.test(trimmed)) {
        const separator = trimmed.includes('.') ? '.' : trimmed.includes('/') ? '/' : '-';
        const parts = trimmed.split(separator);
        return normalizeParts(parts, 'DMY');
      }

      const parsed = new Date(trimmed);
      if (!isNaN(parsed)) {
        return parsed.toISOString().split('T')[0];
      }
    }

    return '';
  };

  const formatDateForDisplay = (value) => {
    const normalized = normalizeDateForInput(value);
    if (!normalized) return '-';

    const [year, month, day] = normalized.split('-');
    if (year && month && day) {
      return `${day}.${month}.${year}`;
    }

    return normalized;
  };
  const handleAddressFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'telefon') {
      handlePhoneInput(e);
      return;
    }

    if (name === "il") {
      const secilenIl = iller.find((il) => il.il_adi === value);
      setIlceler(secilenIl ? secilenIl.ilceler : []);
      setAddressForm(prev => ({
        ...prev,
        [name]: value,
        ilce: ""
      }));
    } else if (name === "ulke_kodu") {
      // Country code is handled by CountrySelector onSelect
      return;
    } else {
      setAddressForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();

    // Adres uzunluğu kontrolü
    if (addressForm.addressText.trim().length < 10) {
      showToast("Adres en az 10 karakter olmalıdır", "error");
      return;
    }

    if (!/^\d{11}$/.test(addressForm.tc)) {
      showToast("TC Kimlik numarası 11 haneli olmalıdır", "error");
      return;
    }

    if (!addressForm.dogum_tarihi) {
      showToast("Doğum tarihi alanı zorunludur", "error");
      return;
    }

    try {
      if (editingAddress) {
        // await updateAddress(editingAddress, addressForm);
        // const updatedAddresses = await getAddresses();
        // setAddresses(updatedAddresses);
        await updateAddress({ addressId: editingAddress, addressData: addressForm }).unwrap();
        refetchAddresses();
        showToast("Adres başarıyla güncellendi", "success");
      } else {
        // await addAddress(addressForm);
        // const updatedAddresses = await getAddresses();
        // setAddresses(updatedAddresses);
        await addAddress(addressForm).unwrap();
        refetchAddresses();
        showToast("Yeni adres başarıyla eklendi", "success");
      }
      closeAddressModal();
    } catch (error) {
      console.error("Adres işlemi başarısız:", error);
      showToast("Adres işlemi sırasında bir hata oluştu", "error");
    }
  };

  const openDeleteModal = (address) => {
    setAddressToDelete(address);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setAddressToDelete(null);
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDelete) return;

    try {
      // await deleteAddress(addressToDelete.id);
      // const updatedAddresses = await getAddresses();
      // setAddresses(updatedAddresses);
      await deleteAddress(addressToDelete.id).unwrap();
      refetchAddresses();
      showToast("Adres başarıyla silindi", "success");
      closeDeleteModal();
    } catch (error) {
      console.error("Adres silme işlemi başarısız:", error);
      showToast("Adres silinirken bir hata oluştu", "error");
    }
  };

  // Adres alanı onblur kontrolü
  const handleAddressBlur = () => {
    if (addressForm.addressText.trim().length < 10) {
      showToast("Adres en az 10 karakter olmalıdır", "error");
    }
  };

  if (isLoadingAddresses) {
    return (
      <div className="user-loading-container">
        <div className="spinner-container">
          <Loader size={40}/>
        </div>
      </div>
    );
  }

  return (
    <div className="user-addresses-container">
      <div className="address-header">
        <div className="user-address-actions">
          <button
            className="user-add-address-button"
            onClick={() => openAddressModal()}
          >
            <FaPlus size={14} /> Yeni Adres Ekle
          </button>
        </div>
      </div>

      <div className="user-address-list">
        {addresses && addresses.length > 0 ? (
          addresses.map(address => (
            <div className="user-address-card" key={address.id}>
              <h4 className="d-flex justify-content-between">
                <span className="user-address-title">
                  <FaHome className="me-2" /> {address.title}
                </span>
                <span className={`user-address-type ${address.adres_tipi === "Fatura Adresi" ? "billing" : "delivery"}`}>
                  {address.adres_tipi || "Teslimat Adresi"}
                </span>
              </h4>
              <p>
                {address.ad} {address.soyad} <br />
                {address.addressText} <br />
                {address.ilce}, {address.il} <br />
                {address.telefon}
              </p>
              <p>
                TC: {address.tc || "-"} <br />
                Doğum Tarihi: {formatDateForDisplay(address.dogum_tarihi)}
              </p>
              <div className="user-address-actions">
                <button
                  className="user-edit-address-button"
                  onClick={() => openAddressModal(address)}
                >
                  <FaEdit size={14} /> Düzenle
                </button>
                <button
                  className="user-delete-address-button"
                  onClick={() => openDeleteModal(address)}
                >
                  <FaTrash size={14} /> Sil
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="user-no-address-message">
            <FaMapMarkerAlt size={24} className="mb-2" />
            <p>Henüz kayıtlı adresiniz bulunmamaktadır.</p>
            <p>Alışveriş yaparken kolaylık sağlamak için adres ekleyebilirsiniz.</p>
          </div>
        )}
      </div>

      {/* Adres Ekleme/Düzenleme Modalı */}
      {showAddressModal && (
        <div className="user-address-modal-overlay">
          <div className="user-address-modal">
            <h3>{editingAddress ? "Adresi Düzenle" : "Yeni Adres Ekle"}</h3>
            <form onSubmit={handleAddressSubmit} className="user-address-modal-form">
              <div className="user-form-user">
                <label>Adres Başlığı</label>
                <input
                  type="text"
                  name="title"
                  value={addressForm.title}
                  onChange={handleAddressFormChange}
                  placeholder="Örn: Ev, İş"
                  required
                />
              </div>

              <div className="user-form-user">
                <label>Adres Tipi</label>
                <select
                  name="adres_tipi"
                  value={addressForm.adres_tipi}
                  onChange={handleAddressFormChange}
                  required
                >
                  <option value="Teslimat Adresi">Teslimat Adresi</option>
                  <option value="Fatura Adresi">Fatura Adresi</option>
                </select>
              </div>

              <div className="user-form-row">
                <div className="user-form-user">
                  <label>Ad</label>
                  <input
                    type="text"
                    name="ad"
                    value={addressForm.ad}
                    onChange={handleAddressFormChange}
                    placeholder="Alıcı ad"
                    required
                  />
                </div>
                <div className="user-form-user">
                  <label>Soyad</label>
                  <input
                    type="text"
                    name="soyad"
                    value={addressForm.soyad}
                    onChange={handleAddressFormChange}
                    placeholder="Alıcı soyad"
                    required
                  />
                </div>
              </div>

              <div className="user-form-row">
                <div className="user-form-user">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={addressForm.email}
                    onChange={handleAddressFormChange}
                    placeholder="Email"
                    required
                  />
                </div>
                  <div className="user-form-user">
                  <label>Telefon</label>
                  <div className="phone-input-group">
                    <CountrySelector
                      selectedCountry={selectedCountry}
                      onSelect={(country) => {
                        setSelectedCountry(country);
                        setAddressForm(prev => ({ ...prev, telefon: '', ulke_kodu: country.code }));
                      }}
                      countries={countryCodes}
                    />
                    <input
                      type="tel"
                      name="telefon"
                      value={addressForm.telefon}
                      onChange={handleAddressFormChange}
                      onKeyDown={handlePhoneKeyDown}
                      placeholder={selectedCountry.mobile_number_length ? `${selectedCountry.mobile_number_length} haneli` : "Telefon"}
                      maxLength={selectedCountry.mobile_number_length || 15}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="user-form-row">
                <div className="user-form-user">
                  <label>TC Kimlik</label>
                  <input
                    type="text"
                    name="tc"
                    value={addressForm.tc}
                    onChange={handleAddressFormChange}
                    placeholder="11 haneli TC kimlik"
                    maxLength="11"
                    required
                  />
                </div>
                <div className="user-form-user">
                  <label>Doğum Tarihi</label>
                  <input
                    type="date"
                    name="dogum_tarihi"
                    value={addressForm.dogum_tarihi}
                    onChange={handleAddressFormChange}
                    required
                  />
                </div>
              </div>

              <div className="user-form-row">
                <div className="user-form-user">
                  <label>İl</label>
                  <select
                    name="il"
                    value={addressForm.il}
                    onChange={handleAddressFormChange}
                    required
                  >
                    <option value="">İl Seçiniz</option>
                    {iller.map((il, index) => (
                      <option key={index} value={il.il_adi}>
                        {il.il_adi}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="user-form-user">
                  <label>İlçe</label>
                  <select
                    name="ilce"
                    value={addressForm.ilce}
                    onChange={handleAddressFormChange}
                    required
                    disabled={!addressForm.il}
                  >
                    <option value="">İlçe Seçiniz</option>
                    {ilceler.map((ilce, index) => (
                      <option key={index} value={ilce.ilce_adi}>
                        {ilce.ilce_adi}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="user-form-user">
                <label>Adres</label>
                <textarea
                  name="addressText"
                  value={addressForm.addressText}
                  onChange={handleAddressFormChange}
                  onBlur={handleAddressBlur}
                  placeholder="Adresinizi giriniz"
                  required
                ></textarea>
              </div>
              <div className="user-address-modal-actions">
                <button type="button" className="user-cancel-button" onClick={closeAddressModal}>
                  İptal
                </button>
                <button
                  type="submit"
                  className="user-save-address-button"
                  disabled={isAddingAddress || isUpdatingAddress}
                >
                  {isAddingAddress || isUpdatingAddress ? "Kaydediliyor..." : editingAddress ? "Güncelle" : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adres Silme Onay Modalı */}
      {showDeleteModal && addressToDelete && (
        <div className="user-address-modal-overlay">
          <div className="user-address-modal delete-modal">
            <div className="delete-modal-content">
              <div className="delete-icon">
                <FaExclamationTriangle size={36} color="#f44336" />
              </div>
              <h3>Adresi Sil</h3>
              <p>"{addressToDelete.title}" adresini silmek istediğinizden emin misiniz?</p>
              <p className="delete-warning">Bu işlem geri alınamaz!</p>

              <div className="user-address-modal-actions">
                <button type="button" className="user-cancel-button" onClick={closeDeleteModal}>
                  İptal
                </button>
                <button
                  type="button"
                  className="user-delete-confirm-button"
                  onClick={confirmDeleteAddress}
                  disabled={isDeletingAddress}
                >
                  {isDeletingAddress ? "Siliniyor..." : "Evet, Sil"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressComponent; 